import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  FileText,
  Receipt,
  UserX,
  Cake,
  Calendar,
  Mail,
  Phone,
  Clock,
  X,
  ChevronRight,
  AlertCircle,
  AlertTriangle,
  Info,
  RefreshCw,
  Send,
  Loader2,
  Check,
  Wrench
} from '../../icons/wp-icons';
import { proaktiveAktionenApi } from '../../api/proaktiveAktionenApi';
import ProaktiveAktionModal from './ProaktiveAktionModal';

// Icons für Aktionstypen
const AKTION_ICONS = {
  KVA_NACHFASSEN: FileText,
  RECHNUNG_MAHNUNG: Receipt,
  KUNDE_INAKTIV: UserX,
  GEBURTSTAG: Cake,
  TERMIN_ERINNERUNG: Calendar,
  WARTUNG_FAELLIG: Wrench
};

// Labels für Aktionstypen
const AKTION_LABELS = {
  KVA_NACHFASSEN: 'KVA nachfassen',
  RECHNUNG_MAHNUNG: 'Mahnung',
  KUNDE_INAKTIV: 'Inaktiver Kunde',
  GEBURTSTAG: 'Geburtstag',
  TERMIN_ERINNERUNG: 'Termine',
  WARTUNG_FAELLIG: 'Wartung'
};

// Prioritäts-Farben
const PRIORITAET_STYLES = {
  HOCH: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    badge: 'bg-red-100 text-red-700',
    icon: AlertCircle,
    dot: 'bg-red-500',
    leftBar: 'border-l-red-500'
  },
  MITTEL: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    badge: 'bg-yellow-100 text-yellow-700',
    icon: AlertTriangle,
    dot: 'bg-yellow-500',
    leftBar: 'border-l-yellow-500'
  },
  NIEDRIG: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    badge: 'bg-green-100 text-green-700',
    icon: Info,
    dot: 'bg-green-500',
    leftBar: 'border-l-green-500'
  }
};

function ProaktiveAktionenWidget() {
  const navigate = useNavigate();
  const [aktionen, setAktionen] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAktion, setSelectedAktion] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    loadAktionen();
  }, []);

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Container-basierte Breakpoints (das Widget ist im Grid oft schmaler als der Screen)
  const isNarrow = containerWidth > 0 && containerWidth < 360;
  const isRoomy = containerWidth >= 480;

  const loadAktionen = async () => {
    try {
      setLoading(true);
      const [aktionenRes, statsRes] = await Promise.all([
        proaktiveAktionenApi.getAktionen({ limit: 10 }),
        proaktiveAktionenApi.getStats()
      ]);
      setAktionen(aktionenRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Fehler beim Laden der proaktiven Aktionen:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIgnorieren = async (e, aktionId) => {
    e.stopPropagation();
    try {
      setActionLoading(aktionId);
      await proaktiveAktionenApi.ignorieren(aktionId);
      setAktionen(prev => prev.filter(a => a.id !== aktionId));
      setStats(prev => prev ? { ...prev, gesamt: prev.gesamt - 1 } : prev);
    } catch (error) {
      console.error('Fehler beim Ignorieren:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleZurueckstellen = async (e, aktionId) => {
    e.stopPropagation();
    // Zurückstellen um 3 Tage
    const bis = new Date();
    bis.setDate(bis.getDate() + 3);

    try {
      setActionLoading(aktionId);
      await proaktiveAktionenApi.zurueckstellen(aktionId, bis.toISOString());
      setAktionen(prev => prev.filter(a => a.id !== aktionId));
      setStats(prev => prev ? { ...prev, gesamt: prev.gesamt - 1 } : prev);
    } catch (error) {
      console.error('Fehler beim Zurückstellen:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAktionErledigt = (aktionId) => {
    setAktionen(prev => prev.filter(a => a.id !== aktionId));
    setStats(prev => prev ? { ...prev, gesamt: prev.gesamt - 1 } : prev);
    setSelectedAktion(null);
  };

  // Schnellaktion ausführen (E-Mail senden, Mahnung erstellen, etc.)
  const handleSchnellAktion = async (e, aktion, data = {}) => {
    e.stopPropagation();
    try {
      setActionLoading(aktion.id);
      const response = await proaktiveAktionenApi.schnellAktion(aktion.id, data);

      if (response.data.success) {
        // Bei OPEN_PHONE: Telefon-Link öffnen
        if (response.data.action === 'OPEN_PHONE' && response.data.telefon) {
          window.location.href = `tel:${response.data.telefon}`;
          // Aktion nicht entfernen, da nur Telefon geöffnet wurde
        } else {
          // Aktion aus Liste entfernen
          setAktionen(prev => prev.filter(a => a.id !== aktion.id));
          setStats(prev => prev ? { ...prev, gesamt: prev.gesamt - 1 } : prev);
        }
      } else {
        alert(response.data.message || 'Aktion konnte nicht ausgeführt werden');
      }
    } catch (error) {
      console.error('Fehler bei Schnellaktion:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Fehler beim Ausführen der Aktion';
      alert(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  // Action Button Label und Icon basierend auf vorgeschlagener Aktion
  const getQuickActionButton = (aktion) => {
    const isLoading = actionLoading === aktion.id;
    const btnClass = `inline-flex items-center gap-1.5 bg-primary text-white px-2.5 py-1.5 rounded-button text-xs font-medium hover:bg-primary-dark transition whitespace-nowrap ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`;
    const btnClassSecondary = btnClass.replace('bg-primary', 'bg-gray-600').replace('hover:bg-primary-dark', 'hover:bg-gray-700');

    // Wartung: Zwei Buttons (Email + Telefon)
    if (aktion.vorgeschlageneAktion === 'WARTUNG_TERMIN_VEREINBAREN') {
      return (
        <>
          <button
            onClick={(e) => handleSchnellAktion(e, aktion, { kanal: 'email' })}
            disabled={isLoading}
            className={btnClass}
            title="Per Email erinnern"
          >
            {isLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
            ) : (
              <Mail className="w-3.5 h-3.5 shrink-0" />
            )}
            {!isNarrow && <span>Email</span>}
          </button>
          <button
            onClick={(e) => handleSchnellAktion(e, aktion, { kanal: 'telefon' })}
            disabled={isLoading}
            className={btnClassSecondary}
            title="Telefonisch erinnern"
          >
            {isLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
            ) : (
              <Phone className="w-3.5 h-3.5 shrink-0" />
            )}
            {!isNarrow && <span>Anrufen</span>}
          </button>
        </>
      );
    }

    // labelShort = kompakt für enge Breite, label = voll
    const buttonConfig = {
      EMAIL_SENDEN: { icon: Send, label: 'Erinnerung senden', labelShort: 'Erinnern' },
      MAHNUNG_SENDEN: { icon: Receipt, label: 'Mahnung senden', labelShort: 'Mahnung' },
      ANRUFEN: { icon: Phone, label: 'Anrufen', labelShort: 'Anrufen' },
      SMS_SENDEN: { icon: Send, label: 'SMS senden', labelShort: 'SMS' },
      GLUECKWUNSCH_SENDEN: { icon: Cake, label: 'Grüße senden', labelShort: 'Grüße' }
    };

    const config = buttonConfig[aktion.vorgeschlageneAktion];
    if (!config) return null;

    const Icon = config.icon;
    const visibleLabel = isRoomy ? config.label : config.labelShort;

    return (
      <button
        onClick={(e) => handleSchnellAktion(e, aktion)}
        disabled={isLoading}
        className={btnClass}
        title={config.label}
      >
        {isLoading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
        ) : (
          <Icon className="w-3.5 h-3.5 shrink-0" />
        )}
        {!isNarrow && <span>{visibleLabel}</span>}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!aktionen || aktionen.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500 space-y-2">
        <Bot className="w-12 h-12 text-gray-300" />
        <p>Keine offenen Vorschläge</p>
        <p className="text-sm text-gray-400">Alles erledigt!</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full flex flex-col">
      {/* Header mit Badges + Refresh */}
      <div className="flex items-center justify-between gap-2 mb-3 flex-shrink-0">
        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
          {stats && (
            <>
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium whitespace-nowrap"
                title={`${stats.gesamt} offene Aktionen`}
              >
                {stats.gesamt}
                {!isNarrow && <span className="text-gray-500">offen</span>}
              </span>
              {stats.nachPrioritaet?.HOCH > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium whitespace-nowrap"
                  title={`${stats.nachPrioritaet.HOCH} dringend`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                  {stats.nachPrioritaet.HOCH}
                  {!isNarrow && <span>dringend</span>}
                </span>
              )}
            </>
          )}
        </div>
        <button
          onClick={loadAktionen}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition shrink-0"
          title="Aktualisieren"
        >
          <RefreshCw className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Aktionen Liste */}
      <div className="flex-1 overflow-y-auto space-y-2">
        {aktionen.map((aktion) => {
          const TypeIcon = AKTION_ICONS[aktion.typ] || Bot;
          const style = PRIORITAET_STYLES[aktion.prioritaet] || PRIORITAET_STYLES.MITTEL;
          const isLoading = actionLoading === aktion.id;

          return (
            <div
              key={aktion.id}
              onClick={() => setSelectedAktion(aktion)}
              className={`relative p-2.5 rounded-lg border border-l-4 cursor-pointer transition hover:shadow-md ${style.bg} ${style.border} ${style.leftBar} ${isLoading ? 'opacity-50' : ''}`}
            >
              {/* Titel-Zeile: Icon + Titel/Kunde nehmen volle Breite */}
              <div className="flex items-start gap-2">
                <div className="p-1.5 bg-white rounded-lg flex-shrink-0">
                  <TypeIcon className="w-3.5 h-3.5 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 line-clamp-2">
                    {aktion.titel}
                  </p>
                  {aktion.kunde && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {aktion.kunde.name}
                    </p>
                  )}
                </div>
              </div>

              {/* Action-Zeile: Quick-Action + Sekundäre Buttons rechts */}
              <div className="flex flex-wrap items-center gap-1.5 mt-2 pl-9">
                {getQuickActionButton(aktion)}
                <div className="ml-auto flex items-center gap-0.5">
                  <button
                    onClick={(e) => handleZurueckstellen(e, aktion.id)}
                    className="p-1.5 hover:bg-white/80 rounded transition"
                    title="3 Tage zurückstellen"
                    disabled={isLoading}
                  >
                    <Clock className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                  <button
                    onClick={(e) => handleIgnorieren(e, aktion.id)}
                    className="p-1.5 hover:bg-white/80 rounded transition"
                    title="Ignorieren"
                    disabled={isLoading}
                  >
                    <X className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAktion(aktion);
                    }}
                    className="p-1.5 hover:bg-white/80 rounded transition"
                    title="Details"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Alle anzeigen Link */}
      {stats && stats.gesamt > aktionen.length && (
        <button
          onClick={() => navigate('/einstellungen?tab=allgemein')}
          className="mt-3 text-sm text-primary hover:text-primary-dark font-medium flex items-center gap-1 justify-center"
        >
          Alle {stats.gesamt} anzeigen
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {/* Modal */}
      {selectedAktion && (
        <ProaktiveAktionModal
          aktion={selectedAktion}
          onClose={() => setSelectedAktion(null)}
          onErledigt={handleAktionErledigt}
        />
      )}
    </div>
  );
}

export default ProaktiveAktionenWidget;
